import { useEffect, useState, type ReactNode } from 'react';
import type { ConversationSummary } from '@zenda/api-contract';
import type { Dictionary } from '../content/pt-BR';
import { Icon } from './Icon';
import { formatWaiting, initials } from '../lib/format';
import '../styles/piloto.css';

/** O relógio que anda de segundo em segundo enquanto há prazo na tela. */
export function useAgora(ativo: boolean): number {
  const [agora, setAgora] = useState(() => Date.now());
  useEffect(() => {
    if (!ativo) return;
    const r = setInterval(() => setAgora(Date.now()), 1000);
    return () => clearInterval(r);
  }, [ativo]);
  return agora;
}

export const pilotoAtende = (c: ConversationSummary): boolean =>
  c.autopilot.mode !== 'off' && !c.autopilot.noAutopilot && c.autopilot.state === 'responding';

/**
 * O piloto na lista mora no quadrado das iniciais, e não numa linha a mais (plano §6.2):
 * amarelo é "Precisa de você", o ícone de IA é a conversa que o piloto atende e o de pessoa
 * é a assumida. O motivo em uma frase fica na dica, e por extenso na faixa da conversa.
 */
export function pilotoNoAvatar(
  t: Dictionary,
  c: ConversationSummary,
): { readonly classe: string; readonly conteudo: ReactNode; readonly dica: string | null } {
  const p = c.autopilot;
  const comMotivo = (rotulo: string) => (p.reason === null ? rotulo : `${rotulo}: ${p.reason}`);
  // O vermelho só conta o horário de trabalho dela: às 3h da manhã nada atrasa (plano §6.2).
  const atrasada =
    p.overdue && p.businessMinutesWaiting !== null
      ? ` · ${t.piloto.atrasada(formatWaiting(p.businessMinutesWaiting))}`
      : '';
  if (p.state === 'needs_human') {
    return {
      classe: ` conv__av--precisa${p.overdue ? ' is-atrasada' : ''}`,
      conteudo: initials(c.contactName),
      dica: comMotivo(t.piloto.estados.needs_human) + atrasada,
    };
  }
  if (p.state === 'taken_over') {
    return {
      classe: ` conv__av--assumida${p.overdue ? ' is-atrasada' : ''}`,
      conteudo: <Icon name="user" size={15} />,
      dica: comMotivo(t.piloto.estados.taken_over) + atrasada,
    };
  }
  if (pilotoAtende(c)) {
    return {
      classe: ' conv__av--piloto',
      conteudo: <Icon name="robot" size={16} strokeWidth={1.8} />,
      dica: `${t.piloto.modos[p.mode]}: ${t.piloto.modosLongos[p.mode]}`,
    };
  }
  return { classe: '', conteudo: initials(c.contactName), dica: null };
}
